import React from 'react';
import { 
  Box, 
  Text, 
  VStack, 
  HStack, 
  Flex, 
  useColorModeValue 
} from '@chakra-ui/react';
import { FiCalendar } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { TaskItem } from '../tasks/TaskItem';
import { Task } from '../../../types/task';
import { CalendarEvent } from '../../../utils/calendarUtils'; // Assuming CalendarEvent is defined here

interface EventListProps {
  date: Date;
  events: CalendarEvent[];
  onToggleDone: (task: Task, done: boolean) => void;
  onDelete: (id: string) => void;
  onSelect: (task: Task) => void;
}

export const EventList: React.FC<EventListProps> = ({ 
  date, 
  events,
  onToggleDone,
  onDelete,
  onSelect,
}) => {
  const { t } = useTranslation(['v0/calendar', 'v0/common']);
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  
  // Format date for display (consider using date-fns format if needed)
  const displayDate = date.toLocaleDateString(undefined, { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric', 
    weekday: 'long' 
  });

  return (
    <Box 
      bg={bgColor} 
      borderRadius="lg" 
      shadow="sm" 
      borderWidth="1px"
      borderColor={borderColor}
      height="full" // Ensure it takes full height if parent allows
      display="flex" // Use flex column to manage height
      flexDirection="column"
      overflow="hidden" // Hide overflow
    >
      <Box 
        p={4} 
        borderBottomWidth="1px" 
        borderColor={borderColor}
        bg={useColorModeValue('gray.50', 'gray.900')}
        flexShrink={0} // Prevent header from shrinking
      >
        <HStack spacing={3} align="center">
          <Box 
            as={FiCalendar} 
            size="20px" 
            color={useColorModeValue('pink.500', 'pink.300')} 
          />
          <Text fontSize="lg" fontWeight="medium" color={textColor}>
            {t('v0/calendar:eventList.date', { date: displayDate })}
          </Text>
        </HStack>
      </Box>
      
      {/* Make the list scrollable */}
      <Box flexGrow={1} overflowY="auto" p={2}> 
        <VStack spacing={0} align="stretch">
          {events.length > 0 ? (
            events.map(event => (
              event.task ? ( // Check if task exists on the event
                <Box key={event.id} p={1}>
                  <TaskItem
                    task={event.task}
                    onSelect={onSelect}
                    onToggleDone={onToggleDone}
                    onDelete={onDelete}
                  />
                </Box>
              ) : null // Render nothing if event.task is missing
            ))
          ) : (
            <Flex 
              direction="column" 
              align="center" 
              justify="center" 
              py={12}
              minHeight="200px" // Ensure empty state takes some space
              color={mutedTextColor}
            >
              <Box as={FiCalendar} size="40px" mb={3} opacity={0.5} />
              <Text>{t('v0/calendar:eventList.empty')}</Text>
            </Flex>
          )}
        </VStack>
      </Box>
    </Box>
  );
}; 